import React, { useEffect, useState } from "react";
import { MdArrowBack, MdEmail } from "react-icons/md";
import { FaShieldAlt } from "react-icons/fa";
import { Upload, Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import simpleRenovate from "../../../assets/img/SimpleRenovate.svg";
import AdditionalVerification from "../../../assets/img/Additional Verification.svg";
import ContractorLicenseVerification from "../../../assets/img/Contractor License Verification.svg";
import BusinessVerification from "../../../assets/img/Business Verification.svg";
import { showToast } from "../../../Helper/Toast";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";
import { hitGetProfileApi } from "../../../Redux/Professional/GetProfileSlice";
import { hitUpdateProfileProfessionalApi } from "../../../Redux/Professional/UpdateProfileProfessionalSlice";
import { hitUploadMedia } from "../../../Redux/Common/UploadMediaSlice";
import Loader from "../../Loader/Loader";
import "./GetVerified.scss";

const ACCEPTED_BUSINESS_DOCS = [
  "EIN letter from IRS",
  "Articles of Organization",
  "Certificate of Incorporation",
  "Business registration documents",
];

const US_STATES = [
  { value: "AZ", label: "Arizona" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "TX", label: "Texas" },
];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const VerificationStepper = ({ currentStep }) => (
  <div className="gv-stepper">
    {[1, 2, 3].map((step, index) => (
      <React.Fragment key={step}>
        <div
          className={[
            "gv-stepper__dot",
            currentStep > step && "gv-stepper__dot--done",
            currentStep === step && "gv-stepper__dot--active",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {step}
        </div>
        {index < 2 && (
          <div
            className={[
              "gv-stepper__line",
              currentStep > step && "gv-stepper__line--done",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const PoweredByFooter = ({
  onNext,
  nextLabel = "Next",
  disabled = false,
}) => (
  <div className="gv-wizard__footer">
    <div className="gv-wizard__powered">
      <span>Verification Powered by</span>
      <img src={simpleRenovate} alt="SimpleRenovate" />
    </div>
    <button
      type="button"
      className="gv-wizard__next"
      onClick={onNext}
      disabled={disabled}
    >
      {nextLabel}
    </button>
  </div>
);

const GetVerified = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setGlobalLoading } = useGlobalState();
  const [loading, setLoading] = useState(true);

  const [contractorStep, setContractorStep] = useState(0);
  const [contractorStatus, setContractorStatus] = useState(0);
  const [contractorForm, setContractorForm] = useState({
    email: "",
    licenseNumber: "",
    issueState: "",
    legalName: "",
    businessName: "",
    documents: "",
  });
  const [licenseDocument, setLicenseDocument] = useState("");
  const [licenseUploadFile, setLicenseUploadFile] = useState(null);
  const [emailError, setEmailError] = useState("");

  const [businessStep, setBusinessStep] = useState(0);
  const [businessStatus, setBusinessStatus] = useState(0);
  const [businessForm, setBusinessForm] = useState({
    email: "",
    documents: "",
  });
  const [businessDocument, setBusinessDocument] = useState("");
  const [businessUploadFile, setBusinessUploadFile] = useState(null);
  const [businessEmailError, setBusinessEmailError] = useState("");

  const responseProfileProfessional = useSelector(
    (state) => state.getProfileProfessionalReducer.data,
  );

  useEffect(() => {
    if (!localStorage.getItem("professionalToken")) {
      navigate("/professional/login");
      return;
    }
    setGlobalLoading(true);
    dispatch(hitGetProfileApi());
  }, [dispatch, navigate, setGlobalLoading]);

  useEffect(() => {
    if (responseProfileProfessional?.status !== true) {
      setGlobalLoading(false);
      return;
    }

    const profile = responseProfileProfessional.data;
    setLoading(false);
    setGlobalLoading(false);

    setContractorStatus(profile.isContractorLicenseVerified ?? 0);
    setBusinessStatus(profile.isBusinessVerified ?? 0);

    if (profile.contractorLicenseVerificationData) {
      const data = profile.contractorLicenseVerificationData;
      setContractorForm({
        email: data.email || "",
        licenseNumber: data.licenseNumber || "",
        issueState: data.issueState || "",
        legalName: data.legalName || "",
        businessName: data.businessName || "",
        documents: data.documents || "",
      });
      setLicenseDocument(data.documents || "");
    }

    if (profile.businessVerificationData) {
      const documents = profile.businessVerificationData.documents || "";
      setBusinessForm({
        email: profile.businessVerificationData.email || "",
        documents,
      });
      setBusinessDocument(documents);
    }

    if (profile.isContractorLicenseVerified === 1) {
      setContractorStep(3);
    }
    if (profile.isBusinessVerified === 1) {
      setBusinessStep(3);
    }
  }, [responseProfileProfessional, setGlobalLoading]);

  useEffect(() => {
    if (!licenseUploadFile) return;
    const run = async () => {
      const formData = new FormData();
      formData.append("myfile", licenseUploadFile);
      const response = await dispatch(hitUploadMedia(formData));
      if (response.payload?.status === true) {
        const url = response.payload.data[0].url;
        setLicenseDocument(url);
        setContractorForm((prev) => ({ ...prev, documents: url }));
      } else {
        showToast("Failed to upload license document", "error");
      }
    };
    run();
  }, [licenseUploadFile, dispatch]);

  useEffect(() => {
    if (!businessUploadFile) return;
    const run = async () => {
      const formData = new FormData();
      formData.append("myfile", businessUploadFile);
      const response = await dispatch(hitUploadMedia(formData));
      if (response.payload?.status === true) {
        const url = response.payload.data[0].url;
        setBusinessDocument(url);
        setBusinessForm((prev) => ({ ...prev, documents: url }));
      } else {
        showToast("Failed to upload document", "error");
      }
    };
    run();
  }, [businessUploadFile, dispatch]);

  const handleBack = () => {
    if (contractorStep > 0) {
      if (contractorStep === 1) {
        setContractorStep(0);
        setEmailError("");
      } else {
        setContractorStep((prev) => prev - 1);
      }
      return;
    }
    if (businessStep > 0) {
      if (businessStep === 1) {
        setBusinessStep(0);
        setBusinessEmailError("");
      } else {
        setBusinessStep((prev) => prev - 1);
      }
      return;
    }
    navigate("/professional/profile");
  };

  const startContractorFlow = () => {
    if (contractorStatus === 2) return;
    if (contractorStatus === 1) {
      setContractorStep(3);
    } else {
      setContractorStep(1);
    }
  };

  const startBusinessFlow = () => {
    if (businessStatus === 2) return;
    if (businessStatus === 1) {
      setBusinessStep(3);
    } else {
      setBusinessStep(1);
    }
  };

  const goContractorStep2 = () => {
    if (!contractorForm.email.trim()) {
      setEmailError("Enter a valid email");
      return;
    }
    if (!isValidEmail(contractorForm.email)) {
      setEmailError("Enter a valid email");
      return;
    }
    setEmailError("");
    setContractorStep(2);
  };

  const submitContractor = () => {
    const payload = {
      ...contractorForm,
      legalName: contractorForm.businessName || contractorForm.legalName,
    };
    dispatch(
      hitUpdateProfileProfessionalApi({
        contractorLicenseVerificationData: payload,
      }),
    ).then((response) => {
      if (response.payload?.status === true) {
        showToast("Contractor license verification submitted!", "success");
        setContractorStep(3);
        dispatch(hitGetProfileApi());
      }
    });
  };

  const goBusinessStep2 = () => {
    if (!businessForm.email.trim() || !isValidEmail(businessForm.email)) {
      setBusinessEmailError("Enter a valid email");
      return;
    }
    setBusinessEmailError("");
    setBusinessStep(2);
  };

  const submitBusiness = () => {
    if (!businessDocument) {
      showToast("Please upload business document!", "error");
      return;
    }
    dispatch(
      hitUpdateProfileProfessionalApi({
        businessVerificationData: businessForm,
      }),
    ).then((response) => {
      if (response.payload?.status === true) {
        showToast("Business verification submitted!", "success");
        setBusinessStep(3);
        dispatch(hitGetProfileApi());
      }
    });
  };

  const contractorCanSubmit =
    contractorForm.licenseNumber &&
    contractorForm.issueState &&
    contractorForm.businessName &&
    licenseDocument;

  const renderContractorWizard = () => {
    if (contractorStep === 1) {
      return (
        <section className="gv-wizard">
          <VerificationStepper currentStep={1} />
          <h2 className="gv-wizard__title">Start with your Email</h2>
          <p className="gv-wizard__subtitle">Confirm your work email address</p>

          <label className="gv-field__label" htmlFor="contractor-email">
            Email Address <span className="gv-required">*</span>
          </label>
          <div className="gv-field__with-icon">
            <MdEmail className="gv-field__icon" aria-hidden />
            <input
              id="contractor-email"
              type="email"
              className="gv-field__input"
              value={contractorForm.email}
              onChange={(e) => {
                setContractorForm({ ...contractorForm, email: e.target.value });
                if (emailError) setEmailError("");
              }}
              placeholder="Enter your email"
            />
          </div>
          {emailError && <p className="gv-field__error">{emailError}</p>}

          <p className="gv-wizard__note">
            This email will be used by SimpleRenovate to communicate updates
            about your verification and to help you maintain Verified status.
          </p>

          <PoweredByFooter
            disabled={!contractorForm.email.trim()}
            onNext={goContractorStep2}
          />
        </section>
      );
    }

    if (contractorStep === 2) {
      return (
        <section className="gv-wizard">
          <VerificationStepper currentStep={2} />
          <h2 className="gv-wizard__title">Professional License</h2>
          <p className="gv-wizard__subtitle">
            Provide your license details exactly as they appear on your
            government-issued license.
          </p>

          <label className="gv-field__label" htmlFor="license-number">
            License Number <span className="gv-required">*</span>
          </label>
          <input
            id="license-number"
            type="text"
            className="gv-field__input"
            value={contractorForm.licenseNumber}
            onChange={(e) =>
              setContractorForm({
                ...contractorForm,
                licenseNumber: e.target.value,
              })
            }
            placeholder="Enter your license number"
          />

          <label className="gv-field__label" htmlFor="issue-state">
            Issuing State <span className="gv-required">*</span>
          </label>
          <select
            id="issue-state"
            className="gv-field__input gv-field__select"
            value={contractorForm.issueState}
            onChange={(e) =>
              setContractorForm({
                ...contractorForm,
                issueState: e.target.value,
              })
            }
          >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>

          <label className="gv-field__label" htmlFor="legal-business-name">
            Legal Business Name <span className="gv-required">*</span>
          </label>
          <input
            id="legal-business-name"
            type="text"
            className="gv-field__input"
            value={contractorForm.businessName}
            onChange={(e) =>
              setContractorForm({
                ...contractorForm,
                businessName: e.target.value,
              })
            }
            placeholder="Enter Legal Business Name"
          />
          <p className="gv-field__hint">
            Enter your legal business name as it appears on your documentation to
            avoid rejection (do not enter a DBA).
          </p>

          <div className="gv-upload">
            <div className="gv-upload__icon-wrap" aria-hidden>
              <Upload size={18} strokeWidth={2.2} />
            </div>
            <p className="gv-upload__title">Upload Your License Document</p>
            <label className="gv-upload__choose">
              Choose File
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  setLicenseUploadFile(e.target.files?.[0] || null)
                }
                hidden
              />
            </label>
            <p className="gv-upload__formats">
              Accepted formats PDF, JPG, PNG (Max 10MB)
            </p>
            {licenseDocument && (
              <p className="gv-upload__filename">Document uploaded</p>
            )}
          </div>

          <PoweredByFooter
            disabled={!contractorCanSubmit}
            onNext={submitContractor}
          />
        </section>
      );
    }

    if (contractorStep === 3) {
      return (
        <section className="gv-wizard gv-wizard--complete">
          <VerificationStepper currentStep={3} />
          <div className="gv-thanks">
            <div className="gv-thanks__icon" aria-hidden>
              ✓
            </div>
            <h2>Thank You!</h2>
            <p>
              We will email your verification results within 3 business days.
            </p>
            <button
              type="button"
              className="gv-btn gv-btn--maroon"
              onClick={() => setContractorStep(0)}
            >
              Done
            </button>
          </div>
        </section>
      );
    }

    return null;
  };

  const renderBusinessWizard = () => {
    if (businessStep === 1) {
      return (
        <section className="gv-wizard">
          <VerificationStepper currentStep={1} />
          <h2 className="gv-wizard__title">Start with your Email</h2>
          <p className="gv-wizard__subtitle">Confirm your work email address</p>

          <label className="gv-field__label" htmlFor="business-email">
            Email Address <span className="gv-required">*</span>
          </label>
          <div className="gv-field__with-icon">
            <MdEmail className="gv-field__icon" aria-hidden />
            <input
              id="business-email"
              type="email"
              className="gv-field__input"
              value={businessForm.email}
              onChange={(e) => {
                setBusinessForm({ ...businessForm, email: e.target.value });
                if (businessEmailError) setBusinessEmailError("");
              }}
              placeholder="Enter your email"
            />
          </div>
          {businessEmailError && (
            <p className="gv-field__error">{businessEmailError}</p>
          )}

          <p className="gv-wizard__note">
            This email will be used by SimpleRenovate to communicate updates
            about your verification and to help you maintain Verified status.
          </p>

          <PoweredByFooter
            disabled={!businessForm.email.trim()}
            onNext={goBusinessStep2}
          />
        </section>
      );
    }

    if (businessStep === 2) {
      return (
        <section className="gv-wizard">
          <VerificationStepper currentStep={2} />
          <h2 className="gv-wizard__title">Upload Business Documents</h2>
          <p className="gv-wizard__subtitle">
            Upload one or more documents that prove your business registration
          </p>

          <div className="gv-upload">
            <div className="gv-upload__icon-wrap" aria-hidden>
              <Upload size={18} strokeWidth={2.2} />
            </div>
            <p className="gv-upload__title">Upload Documents</p>
            <label className="gv-upload__choose">
              Choose File
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) =>
                  setBusinessUploadFile(e.target.files?.[0] || null)
                }
                hidden
              />
            </label>
            <p className="gv-upload__formats">
              Accepted formats PDF, JPG, PNG (Max 10MB)
            </p>
            {businessDocument && (
              <div className="gv-upload__preview">
                <img src={businessDocument} alt="Uploaded document" />
              </div>
            )}
          </div>

          <PoweredByFooter
            nextLabel="Submit"
            disabled={!businessDocument}
            onNext={submitBusiness}
          />
        </section>
      );
    }

    if (businessStep === 3) {
      return (
        <section className="gv-wizard gv-wizard--complete">
          <VerificationStepper currentStep={3} />
          <div className="gv-thanks">
            <div className="gv-thanks__icon" aria-hidden>
              ✓
            </div>
            <h2>Thank You!</h2>
            <p>
              We will email your verification results within 3 business days.
            </p>
            <button
              type="button"
              className="gv-btn gv-btn--maroon"
              onClick={() => setBusinessStep(0)}
            >
              Done
            </button>
          </div>
        </section>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div style={{ height: "100vh" }}>
        <Loader />
      </div>
    );
  }

  const contractorLocked = contractorStatus === 1 || contractorStatus === 2;
  const businessLocked = businessStatus === 1 || businessStatus === 2;

  return (
    <div className="pro-app-page pro-app-page--muted get-verified">
      <div className="custom_container get-verified__shell">
        <header className="get-verified__hero">
          <button
            type="button"
            className="get-verified__back"
            aria-label="Go back"
            onClick={handleBack}
          >
            <MdArrowBack size={24} />
          </button>
          <h1 className="get-verified__title">Get Verified</h1>
        </header>

        <section className="gv-panel">
          <div className="gv-panel__intro">
            <img
              src={AdditionalVerification}
              alt=""
              className="gv-panel__intro-icon"
              aria-hidden
            />
            <div>
              <h2>Additional Verification</h2>
              <p>Optional verifications to build trust with homeowners</p>
            </div>
          </div>

          {contractorStep === 0 ? (
            <article className="gv-card">
              <div className="gv-card__head">
                <img
                  src={ContractorLicenseVerification}
                  alt=""
                  className="gv-card__icon"
                  aria-hidden
                />
                <div className="gv-card__copy">
                  <div className="gv-card__title-row">
                    <h3>Contractor License Verification</h3>
                    <span className="gv-card__badge">Optional</span>
                  </div>
                  <p>
                    Manual check against U.S. state license databases. Shows you
                    are a licensed trade professional.
                  </p>
                </div>
              </div>

              <div className="gv-info gv-info--blue">
                <FaShieldAlt className="gv-info__icon" aria-hidden />
                <div>
                  <strong>Shield Badge</strong>
                  <p>
                    Licensed Contractor - Verified by SimpleRenovate will be
                    shown publicly on your profile
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="gv-btn gv-btn--blue"
                onClick={startContractorFlow}
                disabled={contractorLocked}
              >
                {contractorStatus === 2
                  ? "Contractor License Verified"
                  : contractorStatus === 1
                    ? "Contractor License Verification In Progress"
                    : contractorStatus === 3
                      ? "Contractor License Verification Failed"
                      : "Verify Contractor License"}
              </button>
            </article>
          ) : (
            renderContractorWizard()
          )}

          {businessStep === 0 ? (
            <article
              className={[
                "gv-card",
                contractorStep > 0 && "gv-card--outlined",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="gv-card__head">
                <img
                  src={BusinessVerification}
                  alt=""
                  className="gv-card__icon"
                  aria-hidden
                />
                <div className="gv-card__copy">
                  <div className="gv-card__title-row">
                    <h3>Business Verification</h3>
                    <span className="gv-card__badge">Optional</span>
                  </div>
                  <p>
                    Document upload verification to confirm you are a real,
                    registered business.
                  </p>
                </div>
              </div>

              <div className="gv-docs">
                <strong>Accepted documents:</strong>
                <ul>
                  {ACCEPTED_BUSINESS_DOCS.map((doc) => (
                    <li key={doc}>{doc}</li>
                  ))}
                </ul>
              </div>

              <div className="gv-info gv-info--green">
                <Check className="gv-info__icon" size={16} strokeWidth={2.5} aria-hidden />
                <div>
                  <strong>Tick Mark Badge</strong>
                  <p>
                    Business Verification — SimpleRenovate will display the badge
                    near your business name.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="gv-btn gv-btn--maroon"
                onClick={startBusinessFlow}
                disabled={businessLocked}
              >
                {businessStatus === 2
                  ? "Business License Verified"
                  : businessStatus === 1
                    ? "Business License Verification In Progress"
                    : businessStatus === 3
                      ? "Business License Verification Failed"
                      : "Verify Business License"}
              </button>
            </article>
          ) : (
            renderBusinessWizard()
          )}
        </section>
      </div>
    </div>
  );
};

export default GetVerified;
